import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "accounting-analysis-panel" }
const _hoisted_2 = { class: "analysis-doc" }
const _hoisted_3 = { class: "analysis-doc-head" }
const _hoisted_4 = { class: "analysis-doc-head__main" }
const _hoisted_5 = { class: "analysis-meta" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { class: "analysis-section" }
const _hoisted_10 = { class: "analysis-summary-tiles" }
const _hoisted_11 = { class: "analysis-summary-tile" }
const _hoisted_12 = { class: "analysis-summary-tile" }
const _hoisted_13 = { class: "analysis-summary-tile" }
const _hoisted_14 = { class: "analysis-section" }
const _hoisted_15 = { class: "analysis-table analysis-table--cols-4" }
const _hoisted_16 = {
  key: 0,
  colspan: "3"
}
const _hoisted_17 = { class: "analysis-section" }
const _hoisted_18 = {
  key: 0,
  class: "analysis-section__sub"
}
const _hoisted_19 = { class: "analysis-table analysis-table--compare" }
const _hoisted_20 = { class: "analysis-table analysis-table--cols-2 analysis-table-gap" }
const _hoisted_21 = { class: "analysis-table analysis-table--ytd analysis-table-gap" }
const _hoisted_22 = { class: "analysis-section" }
const _hoisted_23 = { class: "analysis-table analysis-table--cols-2" }
const _hoisted_24 = {
  key: 0,
  class: "analysis-note"
}
const _hoisted_25 = { class: "analysis-section" }
const _hoisted_26 = { class: "analysis-section-head" }
const _hoisted_27 = ["aria-expanded"]
const _hoisted_28 = {
  key: 0,
  class: "analysis-help-box"
}
const _hoisted_29 = { class: "analysis-table analysis-table--cols-3" }
const _hoisted_30 = { class: "analysis-section" }
const _hoisted_31 = { class: "analysis-section-head" }
const _hoisted_32 = ["aria-expanded"]
const _hoisted_33 = {
  key: 0,
  class: "analysis-help-box"
}
const _hoisted_34 = { class: "analysis-table analysis-table--cols-4" }
const _hoisted_35 = { class: "analysis-table analysis-table--cols-4 analysis-table-gap" }
const _hoisted_36 = { class: "analysis-reco" }
const _hoisted_37 = { class: "reco-head" }
const _hoisted_38 = { class: "reco-actions" }
const _hoisted_39 = ["disabled"]

import { ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

interface MoneyBox {
  income: number;
  expense: number;
  balance: number;
}

interface AnalysisData {
  title: string;
  defaultTitle?: string;
  memberName: string;
  bizLabel: string;
  year: string;
  month?: string;
  periodLabel: string;
  periodRange?: string;
  hideBojo: boolean;
  totals: MoneyBox;
  living: MoneyBox;
  subsidy: MoneyBox;
  otherSubsidy: MoneyBox;
  labor: {
    requiredPercent: number;
    ratio: number;
    met: boolean;
    income: number;
    expense: number;
    needAmount: number;
    fromWage: boolean;
  };
  ytd: {
    copay: number;
    longterm: number;
    transferIn: number;
    borrow: number;
    transferBorrowSum: number;
  };
  profit: {
    periodTransferIn?: number;
    periodBorrow: number;
    periodEtcOut: number;
    periodRepay: number;
    periodProfitSum: number;
    ytdEtcOut: number;
    ytdRepay: number;
    ytdProfitSum: number;
    budgetEtcOut: number;
    budgetRepay: number;
    budgetProfitSum: number;
    remainBorrow: number;
  };
  food: {
    statBalance: number;
    livingBalance: number;
    violation: number;
    hasViolation: boolean;
    ment: string;
  };
  autoRecommendation: string;
  recommendationTitle: string;
  recommendation: string;
}

type HelpKey = 'transfer' | 'profit' | null;

export default /*@__PURE__*/_defineComponent({
  __name: 'AccountingAnalysisPanel',
  props: {
    data: {},
    memberCode: {},
    year: {},
    month: {},
    bizGubun: {},
    periodType: {},
    startMonth: {},
    endMonth: {}
  },
  emits: ["saved"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const titleDraft = ref('');
const memoDraft = ref('');
const saving = ref(false);
const saveMessage = ref('');
const saveError = ref(false);
const openHelpKey = ref<HelpKey>(null);

const toggleHelp = (key: Exclude<HelpKey, null>) => {
  openHelpKey.value = openHelpKey.value === key ? null : key;
};

watch(
  () => props.data,
  (next) => {
    titleDraft.value = next.recommendationTitle || next.title || '';
    memoDraft.value = next.recommendation || '';
    saveMessage.value = '';
    openHelpKey.value = null;
  },
  { immediate: true }
);

const won = (value: number | undefined) => {
  if (!value) return '-';
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
};

const toneClass = (value: number) => {
  if (value > 0) return 'plus';
  if (value < 0) return 'minus';
  return '';
};

const fillAuto = () => {
  titleDraft.value = props.data.defaultTitle || props.data.title;
  memoDraft.value = props.data.autoRecommendation || '';
};

const saveMemo = async () => {
  if (!titleDraft.value.trim() || !memoDraft.value.trim()) {
    saveError.value = true;
    saveMessage.value = '제목과 권고사항을 입력해 주세요.';
    return;
  }
  saving.value = true;
  saveError.value = false;
  saveMessage.value = '';
  try {
    await bankbookApi.saveAccountingAnalysisMemo(props.memberCode, {
      year: props.year,
      month: props.month,
      bizGubun: props.bizGubun,
      title: titleDraft.value.trim(),
      memo: memoDraft.value.trim(),
      periodType: props.periodType,
      startMonth: props.startMonth,
      endMonth: props.endMonth,
    });
    saveMessage.value = '저장했습니다. PDF에도 반영됩니다.';
    emit('saved');
  } catch (error: any) {
    saveError.value = true;
    saveMessage.value = error.response?.data?.message || error.message || '저장에 실패했습니다.';
  } finally {
    saving.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "analysis-kicker" }, "회계분석보고서", -1)),
          _createElementVNode("h3", null, _toDisplayString(__props.data.title), 1),
          _createElementVNode("p", _hoisted_5, [
            _createTextVNode(_toDisplayString(__props.data.memberName) + " ", 1),
            (__props.data.bizLabel)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, " · " + _toDisplayString(__props.data.bizLabel), 1))
              : _createCommentVNode("", true),
            (__props.data.periodRange)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, " · " + _toDisplayString(__props.data.periodRange), 1))
              : (__props.data.periodLabel)
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, " · " + _toDisplayString(__props.data.periodLabel), 1))
                : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["analysis-verdict", __props.data.labor.met ? 'is-ok' : 'is-warn'])
        }, [
          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "analysis-verdict__label" }, "인건비", -1)),
          _createElementVNode("strong", null, _toDisplayString(__props.data.labor.met ? '충족' : '미충족'), 1)
        ], 2)
      ]),
      _createElementVNode("section", _hoisted_9, [
        _cache[9] || (_cache[9] = _createElementVNode("h4", null, "요약", -1)),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "analysis-summary-tile__label" }, "수입", -1)),
            _createElementVNode("strong", null, _toDisplayString(won(__props.data.totals.income)), 1)
          ]),
          _createElementVNode("div", _hoisted_12, [
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "analysis-summary-tile__label" }, "지출", -1)),
            _createElementVNode("strong", null, _toDisplayString(won(__props.data.totals.expense)), 1)
          ]),
          _createElementVNode("div", _hoisted_13, [
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "analysis-summary-tile__label" }, "잔액", -1)),
            _createElementVNode("strong", {
              class: _normalizeClass(toneClass(__props.data.totals.balance))
            }, _toDisplayString(won(__props.data.totals.balance)), 3)
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_14, [
        _cache[14] || (_cache[14] = _createElementVNode("h4", null, "생계비 현황", -1)),
        _createElementVNode("table", _hoisted_15, [
          _cache[13] || (_cache[13] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", null, "구분"),
              _createElementVNode("th", null, "수입"),
              _createElementVNode("th", null, "지출"),
              _createElementVNode("th", null, "잔액")
            ])
          ], -1)),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[10] || (_cache[10] = _createElementVNode("td", null, "생계비(일반)", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.living.income)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.living.expense)), 1),
              _createElementVNode("td", {
                class: _normalizeClass(toneClass(__props.data.living.balance))
              }, _toDisplayString(won(__props.data.living.balance)), 3)
            ]),
            _createElementVNode("tr", null, [
              _cache[11] || (_cache[11] = _createElementVNode("td", null, "생계비(보조)", -1)),
              (__props.data.hideBojo)
                ? (_openBlock(), _createElementBlock("td", _hoisted_16, "해당없음"))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("td", null, _toDisplayString(won(__props.data.subsidy.income)), 1),
                    _createElementVNode("td", null, _toDisplayString(won(__props.data.subsidy.expense)), 1),
                    _createElementVNode("td", {
                      class: _normalizeClass(toneClass(__props.data.subsidy.balance))
                    }, _toDisplayString(won(__props.data.subsidy.balance)), 3)
                  ], 64))
            ]),
            _createElementVNode("tr", null, [
              _cache[12] || (_cache[12] = _createElementVNode("td", null, "기타보조", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.otherSubsidy.income)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.otherSubsidy.expense)), 1),
              _createElementVNode("td", {
                class: _normalizeClass(toneClass(__props.data.otherSubsidy.balance))
              }, _toDisplayString(won(__props.data.otherSubsidy.balance)), 3)
            ])
          ])
        ]),
        _cache[15] || (_cache[15] = _createElementVNode("p", { class: "analysis-note" }, " 생계비(일반) = 전년도이월금(식재료비) + 식재료비수입 + 직원식재료비수입(어르신과 함께 식사할 경우) ", -1))
      ]),
      _createElementVNode("section", _hoisted_17, [
        _createElementVNode("h4", null, [
          _cache[16] || (_cache[16] = _createTextVNode("1. 인건비비율 보고내역 ", -1)),
          (__props.data.labor.cumulativeLabel)
            ? (_openBlock(), _createElementBlock("span", _hoisted_18, "(" + _toDisplayString(__props.data.labor.cumulativeLabel) + ")", 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("table", _hoisted_19, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", null, _toDisplayString(__props.data.year) + "년 인건비 충족비율", 1),
              _cache[17] || (_cache[17] = _createElementVNode("th", null, "실제 지출비율", -1)),
              _createElementVNode("th", null, _toDisplayString(__props.data.labor.met ? '판정' : '부족금액'), 1)
            ])
          ]),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("td", null, _toDisplayString(__props.data.labor.requiredPercent) + "%", 1),
              _createElementVNode("td", {
                class: _normalizeClass(__props.data.labor.met ? 'is-ok' : 'is-warn')
              }, _toDisplayString(__props.data.labor.ratio) + "%", 3),
              _createElementVNode("td", {
                class: _normalizeClass(__props.data.labor.met ? 'is-ok' : 'is-warn')
              }, _toDisplayString(__props.data.labor.met ? '충족' : won(__props.data.labor.needAmount)), 3)
            ])
          ])
        ]),
        _createElementVNode("table", _hoisted_20, [
          _cache[19] || (_cache[19] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", null, "구분"),
              _createElementVNode("th", null, "금액")
            ])
          ], -1)),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[18] || (_cache[18] = _createElementVNode("td", null, "인건비 수입(본인부담·장기요양·가산금)", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.labor.income)), 1)
            ]),
            _createElementVNode("tr", null, [
              _createElementVNode("td", null, "인건비(직접)" + _toDisplayString(__props.data.labor.fromWage ? ' · 종사자인건비 기준' : ''), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.labor.expense)), 1)
            ])
          ])
        ]),
        _createElementVNode("table", _hoisted_21, [
          _cache[27] || (_cache[27] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", { colspan: "2" }, "누적 수입"),
              _createElementVNode("th", { colspan: "2" }, "누적 지출")
            ])
          ], -1)),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[20] || (_cache[20] = _createElementVNode("td", null, "본인부담금", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.copay)), 1),
              _cache[21] || (_cache[21] = _createElementVNode("td", null, "인건비(직접)", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.labor.expense)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[22] || (_cache[22] = _createElementVNode("td", null, "장기요양급여수입", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.longterm)), 1),
              _createElementVNode("td", null, _toDisplayString(__props.data.labor.fromWage ? '종사자인건비 기준' : '장부 인건비 계정 기준'), 1),
              _cache[23] || (_cache[23] = _createElementVNode("td", null, "-", -1))
            ]),
            _createElementVNode("tr", null, [
              _cache[24] || (_cache[24] = _createElementVNode("td", null, "합계", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.copay + __props.data.ytd.longterm)), 1),
              _cache[25] || (_cache[25] = _createElementVNode("td", null, null, -1)),
              _cache[26] || (_cache[26] = _createElementVNode("td", null, null, -1))
            ])
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_22, [
        _cache[34] || (_cache[34] = _createElementVNode("h4", null, "2. 식재료비 지출내역", -1)),
        _createElementVNode("table", _hoisted_23, [
          _cache[33] || (_cache[33] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", null, "구분"),
              _createElementVNode("th", null, "금액")
            ])
          ], -1)),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[28] || (_cache[28] = _createElementVNode("td", null, "생계비 수입", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.living.income)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[29] || (_cache[29] = _createElementVNode("td", null, "생계비 지출", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.living.expense)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[30] || (_cache[30] = _createElementVNode("td", null, "통계잔액", -1)),
              _createElementVNode("td", {
                class: _normalizeClass(toneClass(__props.data.food.statBalance))
              }, _toDisplayString(won(__props.data.food.statBalance)), 3)
            ]),
            _createElementVNode("tr", null, [
              _cache[31] || (_cache[31] = _createElementVNode("td", null, "생계비잔액", -1)),
              _createElementVNode("td", {
                class: _normalizeClass(toneClass(__props.data.food.livingBalance))
              }, _toDisplayString(won(__props.data.food.livingBalance)), 3)
            ]),
            _createElementVNode("tr", null, [
              _cache[32] || (_cache[32] = _createElementVNode("td", null, "위반금액", -1)),
              _createElementVNode("td", null, _toDisplayString(__props.data.food.hasViolation ? won(__props.data.food.violation) : '-'), 1)
            ])
          ])
        ]),
        (__props.data.food.ment)
          ? (_openBlock(), _createElementBlock("p", _hoisted_24, _toDisplayString(__props.data.food.ment), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("section", _hoisted_25, [
        _createElementVNode("div", _hoisted_26, [
          _cache[36] || (_cache[36] = _createElementVNode("h4", null, "3. 전입 및 차입내역", -1)),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["analysis-help-btn", { active: openHelpKey.value === 'transfer' }]),
            "aria-expanded": openHelpKey.value === 'transfer',
            title: "안내",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleHelp('transfer')))
          }, [
            _createVNode(_component_v_icon, { size: "16" }, {
              default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                _createTextVNode("mdi-help-circle-outline", -1)
              ]))]),
              _: 1
            })
          ], 10, _hoisted_27)
        ]),
        (openHelpKey.value === 'transfer')
          ? (_openBlock(), _createElementBlock("div", _hoisted_28, [...(_cache[37] || (_cache[37] = [
              _createElementVNode("p", { class: "analysis-help-box__title" }, "이 표는 무엇을 보나요?", -1),
              _createElementVNode("ul", null, [
                _createElementVNode("li", null, [
                  _createElementVNode("strong", null, "전입금"),
                  _createTextVNode(" — 법인·본점 등에서 시설로 넣어 준 돈입니다.")
                ]),
                _createElementVNode("li", null, [
                  _createElementVNode("strong", null, "차입금"),
                  _createTextVNode(" — 은행이나 다른 곳에서 빌려 온 돈입니다.")
                ])
              ], -1),
              _createElementVNode("p", { class: "analysis-help-box__title" }, "왜 비어 있나요?", -1),
              _createElementVNode("p", null, " 선택하신 기간에 위와 같은 입금이 장부에 없으면 금액이 비어 있거나 「-」로 보입니다. 오류가 아니라, 그 기간에 전입·차입 입금이 없었다는 뜻입니다. ", -1)
            ]))]))
          : _createCommentVNode("", true),
        _createElementVNode("table", _hoisted_29, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _cache[38] || (_cache[38] = _createElementVNode("th", null, "구분", -1)),
              _createElementVNode("th", null, _toDisplayString(__props.data.periodLabel || `${__props.data.month || ''}월`) + " 금액", 1),
              _cache[39] || (_cache[39] = _createElementVNode("th", null, "누적금액", -1))
            ])
          ]),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[40] || (_cache[40] = _createElementVNode("td", null, "전입금", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodTransferIn)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.transferIn)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[41] || (_cache[41] = _createElementVNode("td", null, "차입금", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodBorrow)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.borrow)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[42] || (_cache[42] = _createElementVNode("td", null, "합계", -1)),
              _createElementVNode("td", null, _toDisplayString(won((__props.data.profit.periodTransferIn || 0) + (__props.data.profit.periodBorrow || 0))), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.transferBorrowSum)), 1)
            ])
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_30, [
        _createElementVNode("div", _hoisted_31, [
          _cache[44] || (_cache[44] = _createElementVNode("h4", null, "4. 매출이익금", -1)),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["analysis-help-btn", { active: openHelpKey.value === 'profit' }]),
            "aria-expanded": openHelpKey.value === 'profit',
            title: "안내",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleHelp('profit')))
          }, [
            _createVNode(_component_v_icon, { size: "16" }, {
              default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                _createTextVNode("mdi-help-circle-outline", -1)
              ]))]),
              _: 1
            })
          ], 10, _hoisted_32)
        ]),
        (openHelpKey.value === 'profit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_33, [...(_cache[45] || (_cache[45] = [
              _createStaticVNode("<p class=\"analysis-help-box__title\">이름과 달리, 무엇을 보나요?</p><p> 「매출이익」처럼 들리지만, 이 칸은 장부에 잡힌 <strong>기타전출금</strong>(시설 밖으로 보낸 돈)과 <strong>원금·이자 상환</strong>(빌린 돈을 갚은 금액)을 모아서 보여 줍니다. </p><ul><li><strong>기타전출금</strong> — 시설에서 밖으로 보낸 돈</li><li><strong>원금 및 이자상환금</strong> — 빌린 원금과 이자를 갚은 돈</li><li><strong>누적잔금</strong> — 올해 빌려 온 금액에서 갚은 금액을 뺀 남은 빚</li></ul><p class=\"analysis-help-box__title\">왜 비어 있나요?</p><p> 선택하신 기간에 전출·상환 지출이 장부에 없으면 금액이 비어 있거나 「-」로 보입니다. 오류가 아니라, 그 기간에 해당 지출이 없었다는 뜻입니다. </p>", 5)
            ]))]))
          : _createCommentVNode("", true),
        _createElementVNode("table", _hoisted_34, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _cache[46] || (_cache[46] = _createElementVNode("th", null, "구분", -1)),
              _createElementVNode("th", null, _toDisplayString(__props.data.periodLabel || `${__props.data.month || ''}월`), 1),
              _cache[47] || (_cache[47] = _createElementVNode("th", null, "누적", -1)),
              _cache[48] || (_cache[48] = _createElementVNode("th", null, "당해년도 예산", -1))
            ])
          ]),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _cache[49] || (_cache[49] = _createElementVNode("td", null, "기타전출금", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodEtcOut)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.ytdEtcOut)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.budgetEtcOut)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[50] || (_cache[50] = _createElementVNode("td", null, "원금 및 이자상환금", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodRepay)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.ytdRepay)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.budgetRepay)), 1)
            ]),
            _createElementVNode("tr", null, [
              _cache[51] || (_cache[51] = _createElementVNode("td", null, "합계", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodProfitSum)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.ytdProfitSum)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.budgetProfitSum)), 1)
            ])
          ])
        ]),
        _createElementVNode("table", _hoisted_35, [
          _cache[54] || (_cache[54] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", null, "구분"),
              _createElementVNode("th", null, "차입금"),
              _createElementVNode("th", null, "원금이자상환"),
              _createElementVNode("th", null, "누적잔금")
            ])
          ], -1)),
          _createElementVNode("tbody", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("td", null, _toDisplayString(__props.data.periodLabel || `${__props.data.month || ''}월`), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodBorrow)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.periodRepay)), 1),
              _cache[52] || (_cache[52] = _createElementVNode("td", null, "-", -1))
            ]),
            _createElementVNode("tr", null, [
              _cache[53] || (_cache[53] = _createElementVNode("td", null, "누적", -1)),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.ytd.borrow)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.ytdRepay)), 1),
              _createElementVNode("td", null, _toDisplayString(won(__props.data.profit.remainBorrow)), 1)
            ])
          ])
        ]),
        _cache[55] || (_cache[55] = _createElementVNode("p", { class: "analysis-note" }, "차입금·원금 및 이자상환은 당해년도 기준이며, 전년도 차입금은 포함하지 않습니다.", -1))
      ]),
      _createElementVNode("section", _hoisted_36, [
        _createElementVNode("div", _hoisted_37, [
          _cache[56] || (_cache[56] = _createElementVNode("h4", null, "권고사항 및 점검내용", -1)),
          _createElementVNode("div", _hoisted_38, [
            _createElementVNode("button", {
              type: "button",
              class: "reco-btn ghost",
              onClick: fillAuto
            }, "자동 멘트"),
            _createElementVNode("button", {
              type: "button",
              class: "reco-btn",
              disabled: saving.value,
              onClick: saveMemo
            }, _toDisplayString(saving.value ? '저장 중...' : '저장'), 9, _hoisted_39)
          ])
        ]),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((titleDraft).value = $event)),
          class: "reco-title",
          type: "text",
          placeholder: "보고서 제목"
        }, null, 512), [
          [_vModelText, titleDraft.value]
        ]),
        _withDirectives(_createElementVNode("textarea", {
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((memoDraft).value = $event)),
          class: "reco-textarea",
          placeholder: "권고사항 및 점검내용을 작성해 주세요."
        }, null, 512), [
          [_vModelText, memoDraft.value]
        ]),
        (saveMessage.value)
          ? (_openBlock(), _createElementBlock("p", {
              key: 0,
              class: _normalizeClass(["reco-status", { error: saveError.value }])
            }, _toDisplayString(saveMessage.value), 3))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})